<template>
  <section
    class="execution"
    :class="{
      loading:
        wfLoading ||
        (history.loading && (!historyEvents || !historyEvents.length)),
      ready: !wfLoading && !history.loading,
    }"
  >
    <navigation-bar>
      <navigation-link
        id="nav-link-summary"
        :label="$t('dj-概览')"
        :to="{
          name: 'workflow/summary',
          params: { clusterName },
        }"
        data-cy="summary-link"
      />
      <navigation-link
        id="nav-link-history"
        :label="$t('dj-历史')"
        :to="{
          name: 'workflow/history',
          params: { clusterName },
        }"
        data-cy="history-link"
      />
      <navigation-link
        v-if="summary.wfStatus && summary.wfStatus.toLowerCase() === 'failed'"
        id="nav-link-configs"
        :label="$t('dj-运行参数')"
        :to="{
          name: 'workflow/configs',
          params: { workflowId, runId, clusterName },
        }"
        data-cy="config-link"
      />
    </navigation-bar>
    <router-view
      name="summary"
      :base-a-p-i-u-r-l="baseAPIURL"
      :date-format="dateFormat"
      :display-workflow-id="displayWorkflowId"
      :domain="domain"
      :input="summary.input"
      :is-workflow-running="summary.isWorkflowRunning"
      :parent-workflow-route="summary.parentWorkflowRoute"
      :result="summary.result"
      :time-format="timeFormat"
      :timezone="timezone"
      :wf-status="summary.wfStatus"
      :workflow="summary.workflow"
      @onNotification="onNotification"
    />
    <router-view
      name="history"
      :base-a-p-i-u-r-l="baseAPIURL"
      :events="historyEvents"
      :is-workflow-running="isWorkflowRunning"
      :loading="history.loading"
      :timeline-events="historyTimelineEvents"
      :workflow-history-event-highlight-list="workflowHistoryEventHighlightList"
      :workflow-history-event-highlight-list-enabled="
        workflowHistoryEventHighlightListEnabled
      "
      @onNotification="onNotification"
      @onWorkflowHistoryEventParamToggle="onWorkflowHistoryEventParamToggle"
    />
    <router-view name="configs" />
    <router-view
      name="pending"
      :base-a-p-i-u-r-l="baseAPIURL"
      :time-format="timeFormat"
      :timezone="timezone"
    />
    <router-view
      name="stacktrace"
      :base-a-p-i-u-r-l="baseAPIURL"
      :date-format="dateFormat"
      :is-worker-running="isWorkerRunning"
      :task-list-name="taskList.name"
      :time-format="timeFormat"
      :timezone="timezone"
      @onNotification="onNotification"
    />
    <router-view
      name="query"
      :base-a-p-i-u-r-l="baseAPIURL"
      :is-worker-running="isWorkerRunning"
      :task-list-name="taskList.name"
      @onNotification="onNotification"
    />
  </section>
</template>
<script>
import { NOTIFICATION_TYPE_ERROR } from "@/constants";
import { getErrorMessage } from "@/helpers";
import NavigationBar from "@/components/navigation-bar.vue";
import NavigationLink from "@/components/navigation-link.vue";
import api from "@/api";
import i18n from "@/assets/i18n/translate-message";
import {
  getHistoryEvents,
  getHistoryTimelineEvents,
  getSummary,
} from "./helpers";
import { RETRY_COUNT_MAX, RETRY_TIMEOUT } from "./constants";

export default {
  components: {
    "navigation-bar": NavigationBar,
    "navigation-link": NavigationLink,
  },
  props: [
    "clusterName",
    "dateFormat",
    "displayWorkflowId",
    "domain",
    "pendingTaskCount",
    "runId",
    "taskListName",
    "timeFormat",
    "timezone",
    "workflow",
    "workflowHistoryEventHighlightList",
    "workflowHistoryEventHighlightListEnabled",
    "workflowId",
  ],
  data() {
    return {
      baseApiUrlRetryCount: 0,
      events: [],
      isWorkflowRunning: undefined,
      nextPageToken: undefined,
      fetchHistoryPageRetryCount: 0,
      wfLoading: true,

      history: {
        loading: undefined,
      },

      summary: {
        input: undefined,
        isWorkflowRunning: undefined,
        parentWorkflowRoute: undefined,
        result: undefined,
        wfStatus: undefined,
        workflow: undefined,
      },

      taskList: {},

      unwatch: [],
    };
  },
  computed: {
    baseAPIURL() {
      const { domain, workflowId, runId } = this;
      return `/api/domains/${domain}/workflows/${workflowId}/${runId}`;
    },
    historyEvents() {
      const {
        clusterName,
        dateFormat,
        events,
        timeFormat,
        timezone,
        workflowHistoryEventHighlightList,
        workflowHistoryEventHighlightListEnabled,
      } = this;

      return getHistoryEvents({
        clusterName,
        dateFormat,
        events,
        timeFormat,
        timezone,
        workflowHistoryEventHighlightList,
        workflowHistoryEventHighlightListEnabled,
      });
    },
    historyTimelineEvents() {
      const { clusterName, historyEvents } = this;

      return getHistoryTimelineEvents({ clusterName, historyEvents });
    },
    historyUrl() {
      const historyUrl = `${this.baseAPIURL}/history?waitForNewEvent=true`;

      if (!this.nextPageToken) {
        return historyUrl;
      }

      return `${historyUrl}&nextPageToken=${encodeURIComponent(
        this.nextPageToken
      )}`;
    },
    isWorkerRunning() {
      return this.taskList.pollers && this.taskList.pollers.length > 0;
    },
  },
  created() {
    this.unwatch.push(
      this.$watch("baseAPIURL", this.onBaseApiUrlChange, { immediate: true })
    );
    this.unwatch.push(
      this.$watch("historyUrl", this.onHistoryUrlChange, { immediate: true })
    );
  },
  beforeDestroy() {
    this.clearWatches();
  },
  methods: {
    clearState() {
      this.events = [];
      this.isWorkflowRunning = undefined;
      this.nextPageToken = undefined;
      this.fetchHistoryPageRetryCount = 0;
      this.wfLoading = true;

      this.history.loading = undefined;

      this.summary.input = undefined;
      this.summary.isWorkflowRunning = undefined;
      this.summary.parentWorkflowRoute = undefined;
      this.summary.result = undefined;
      this.summary.wfStatus = undefined;
      this.summary.workflow = undefined;

      this.$emit("clearWorkflow");
    },
    clearWatches() {
      while (this.unwatch.length) {
        this.unwatch.pop()();
      }
    },
    fetchHistoryPage(pagedHistoryUrl) {
      if (
        this._isDestroyed ||
        !pagedHistoryUrl ||
        this.fetchHistoryPageRetryCount >= RETRY_COUNT_MAX
      ) {
        this.history.loading = false;

        return Promise.resolve();
      }

      this.history.loading = true;
      return api
        .getRunHistoryInfo(this.workflowId, this.runId)
        .then((response) => {
          const res = response.data.response;
          // eslint-disable-next-line no-underscore-dangle
          if (this._isDestroyed) {
            return null;
          }

          if (res.nextPageToken && this.nextPageToken === res.nextPageToken) {
            // nothing happened, and same query is still valid, so let's long pool again
            return this.fetchHistoryPage(pagedHistoryUrl);
          }

          // *** 注意

          if (res.nextPageToken) {
            this.isWorkflowRunning = JSON.parse(
              atob(res.nextPageToken)
            ).IsWorkflowRunning;
            this.nextPageToken = res.nextPageToken;
          } else {
            this.isWorkflowRunning = false;
          }

          const shouldHighlightEventId =
            this.$route.query.eventId &&
            this.events.length <= this.$route.query.eventId;

          // *** 实际调用数据
          const { events } = res.history;

          this.events = this.events.concat(events);

          this.summary = getSummary({
            clusterName: this.clusterName,
            events: this.events,
            isWorkflowRunning: this.isWorkflowRunning,
            workflow: this.workflow,
          });

          if (shouldHighlightEventId) {
            this.$emit("highlight-event-id", this.$route.query.eventId);
          }

          this.fetchHistoryPageRetryCount = 0;

          return this.events;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);

          if (this._isDestroyed) {
            return;
          }

          this.$emit("onNotification", {
            message: getErrorMessage(error),
            type: NOTIFICATION_TYPE_ERROR,
          });

          this.fetchHistoryPageRetryCount += 1;
          setTimeout(
            () => this.fetchHistoryPage(pagedHistoryUrl),
            RETRY_TIMEOUT
          );
        })
        .finally(() => {
          // eslint-disable-next-line no-underscore-dangle
          if (this._isDestroyed || !this.isWorkflowRunning) {
            this.history.loading = false;
          }
        });
    },
    // eslint-disable-next-line consistent-return
    async fetchTaskList() {
      const { taskListName } = this;

      if (!taskListName) {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject("task list name is required");
      }
      const res = await api.getPollersInDomainByName(this.domain, taskListName);
      this.loading = false;
      if (res.data.status === 200 && res.data.response) {
        this.taskList = {
          name: taskListName,
          pollers: Object.keys(res.data.response),
        };
      } else {
        this.taskList = { name: taskListName };
        this.error = `${i18n.t("dj-查询")}${taskListName}${i18n.t(
          "dj-任务列表失败"
        )}`;
      }
    },
    async fetchWorkflowInfo() {
      if (this.baseApiUrlRetryCount >= RETRY_COUNT_MAX) {
        return;
      }
      this.wfLoading = true;
      const res = await api.getWorkflowRunInfoByRunId(
        this.displayWorkflowId,
        this.runId
      );
      this.wfLoading = false;
      const wf = res.data.response;
      this.$emit("setWorkflow", wf);
      this.isWorkflowRunning = !wf.workflowExecutionInfo.closeTime;
      this.baseApiUrlRetryCount = 0;

      // eslint-disable-next-line consistent-return
      return wf;
    },
    onBaseApiUrlChange() {
      this.clearState();
    },
    async onHistoryUrlChange(historyUrl) {
      const workflowInfo = await this.fetchWorkflowInfo();

      if (workflowInfo) {
        await this.fetchHistoryPage(historyUrl);
        this.fetchTaskList();
      }
    },
    onNotification(event) {
      this.$emit("onNotification", event);
    },
    onWorkflowHistoryEventParamToggle(event) {
      this.$emit("onWorkflowHistoryEventParamToggle", event);
    },
  },
};
</script>
